import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { errMessage } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tokenStatus, setTokenStatus] = useState(token ? "checking" : "invalid");

  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/reset-password/validate", { params: { token } });
        if (active) setTokenStatus(data.valid ? "valid" : "invalid");
      } catch {
        if (active) setTokenStatus("invalid");
      }
    })();
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please log in.");
      navigate("/login", { replace: true });
    } catch (err) {
      // Token may have expired/been used between load and submit — show the invalid state.
      setTokenStatus("invalid");
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const InvalidState = (
    <div className="space-y-5" data-testid="reset-invalid">
      <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
        <p className="font-semibold">This password reset link is no longer valid.</p>
        <p className="mt-1">It may have expired or already been used. Please request a new password reset link.</p>
      </div>
      <Link to="/forgot-password" data-testid="reset-request-new">
        <Button className="w-full bg-[#0A2540] text-white hover:bg-[#0c2e4e]">Request a new reset link</Button>
      </Link>
      <Link to="/login" className="block text-center text-sm font-medium text-[#0D9488] hover:underline" data-testid="reset-back-login">
        Back to log in
      </Link>
    </div>
  );

  return (
    <AuthShell
      title="Choose a new password"
      footer={<Link to="/login" className="font-medium text-[#0D9488] hover:underline">Back to log in</Link>}
    >
      {tokenStatus === "checking" ? (
        <div className="flex items-center justify-center py-6 text-slate-500" data-testid="reset-checking">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Verifying your reset link…
        </div>
      ) : tokenStatus === "invalid" ? (
        InvalidState
      ) : (
        <form onSubmit={submit} className="space-y-4" noValidate>
          {error && <div role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          <div className="space-y-1.5">
            <Label htmlFor="password">New password</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">Confirm password</Label>
            <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] text-white hover:bg-[#0c2e4e]" data-testid="reset-submit">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Update password
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
